import { Link } from "react-router-dom";
import { Topbar } from "@/components/parcelio/topbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { conversationsApi, type Conversation, type ConversationMessage } from "@/lib/api";
import { Phone, Video, Send, Paperclip, Package, MessagesSquare } from "lucide-react";
import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LoadingBlock } from "@/components/ui/spinner";
import { useAuth } from "@/stores/auth";

function formatTimestamp(iso: string | null) {
  if (!iso) return "";
  return new Intl.DateTimeFormat("en-NG", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(iso));
}

export default function Chat() {
  const token = useAuth((s) => s.session?.accessToken);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeId, setActiveId] = useState<string | undefined>(undefined);
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);

  const active = conversations.find((c) => c.id === activeId);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const list = await conversationsApi.list(token);
        if (cancelled) return;
        setConversations(list);
        setActiveId((prev) => prev ?? list[0]?.id);
      } catch {
        if (!cancelled) toast.error("Failed to load conversations");
      } finally {
        if (!cancelled) setLoadingConversations(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (!activeId) {
      setMessages([]);
      return;
    }
    let cancelled = false;
    async function loadMessages(id: string) {
      setLoadingMessages(true);
      try {
        const list = await conversationsApi.messages(id, token);
        if (!cancelled) setMessages(list);
      } catch {
        if (!cancelled) toast.error("Failed to load messages");
      } finally {
        if (!cancelled) setLoadingMessages(false);
      }
    }
    loadMessages(activeId);
    return () => {
      cancelled = true;
    };
  }, [activeId, token]);

  async function handleSend(e: React.FormEvent) {
    e.preventDefault();
    const body = draft.trim();
    if (!body || !activeId || sending) return;
    setSending(true);
    try {
      const created = await conversationsApi.postMessage(activeId, body, token);
      setMessages((prev) => [...prev, created]);
      setDraft("");
      toast.success("Message sent");
    } catch {
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  }

  return (
    <>
      <Topbar title="Conversations" subtitle="Every chat tied to a parcel" />
      <div className="flex-1 p-4 md:p-6">
        <div className="surface-card grid overflow-hidden lg:grid-cols-[320px,1fr,300px]" style={{ height: "calc(100dvh - 8rem)" }}>
          {/* Conversation list */}
          <div className="flex flex-col border-r">
            <div className="border-b p-3">
              <Input placeholder="Search conversations…" aria-label="Search conversations" />
            </div>
            <ul className="flex-1 overflow-y-auto">
              {loadingConversations ? <LoadingBlock label="Loading conversations…" className="py-8" /> : null}
              {!loadingConversations && conversations.map((c) => (
                <li key={c.id}>
                  <button
                    onClick={() => setActiveId(c.id)}
                    className={cn(
                      "flex w-full items-start gap-3 border-b px-3 py-3 text-left transition-colors hover:bg-muted/50",
                      activeId === c.id && "bg-muted"
                    )}
                  >
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-soft text-xs font-bold text-primary">
                      {c.counterpart_initials}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate text-sm font-semibold">{c.counterpart_name}</span>
                        <span className="shrink-0 text-[10px] text-muted-foreground">{formatTimestamp(c.last_at)}</span>
                      </div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                        {c.counterpart_role} · {c.parcel_id ?? "—"}
                      </div>
                      <div className="mt-0.5 truncate text-xs text-muted-foreground">{c.last_message}</div>
                    </div>
                    {c.unread > 0 ? (
                      <span className="mt-1 flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-primary px-1.5 text-[10px] font-bold text-primary-foreground">
                        {c.unread}
                      </span>
                    ) : null}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Thread */}
          {active ? (
            <div className="flex min-w-0 flex-col">
              <header className="flex items-center justify-between border-b p-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-bold">
                    {active.counterpart_initials}
                  </div>
                  <div className="min-w-0">
                    <div className="truncate font-semibold">{active.counterpart_name}</div>
                    <div className="text-xs text-muted-foreground">Re: {active.parcel_id ?? "—"}</div>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" aria-label="Call"><Phone className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" aria-label="Video call"><Video className="h-4 w-4" /></Button>
                </div>
              </header>

              <div className="flex-1 space-y-3 overflow-y-auto bg-muted/30 p-4">
                {loadingMessages ? <LoadingBlock label="Loading messages…" /> : null}
                {!loadingMessages && messages.map((m) => (
                  <div key={m.id} className={cn("flex", m.sender === "me" ? "justify-end" : "justify-start")}>
                    <div
                      className={cn(
                        "max-w-[70%] rounded-2xl px-3.5 py-2 text-sm shadow-sm",
                        m.sender === "me"
                          ? "rounded-br-sm bg-primary text-primary-foreground"
                          : "rounded-bl-sm bg-surface text-foreground"
                      )}
                    >
                      <div>{m.body}</div>
                      <div className={cn("mt-1 text-[10px]", m.sender === "me" ? "text-primary-foreground/70" : "text-muted-foreground")}>
                        {formatTimestamp(m.created_at)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <form onSubmit={handleSend} className="flex items-center gap-2 border-t p-3">
                <Button variant="ghost" size="icon" type="button" aria-label="Attach"><Paperclip className="h-4 w-4" /></Button>
                <Input
                  placeholder="Type a message…"
                  aria-label="Message"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  disabled={sending}
                />
                <Button type="submit" size="icon" aria-label="Send" disabled={sending || !draft.trim()}>
                  <Send className="h-4 w-4" />
                </Button>
              </form>
            </div>
          ) : (
            <EmptyThread />
          )}

          {/* Parcel context */}
          {active && active.parcel_id ? (
            <aside className="hidden border-l p-4 lg:block">
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Linked parcel</div>
              <Link
                to={`/app/tracking/${active.parcel_id}`}
                className="mt-3 block rounded-xl border p-3 transition hover:bg-muted/40"
              >
                <div className="flex items-center gap-2">
                  <Package className="h-4 w-4 text-primary" />
                  <span className="font-mono text-xs font-semibold">{active.parcel_id}</span>
                </div>
              </Link>
              <Button variant="outline" size="sm" className="mt-3 w-full" asChild>
                <Link to={`/app/tracking/${active.parcel_id}`}>Open tracking</Link>
              </Button>
            </aside>
          ) : null}
        </div>
      </div>
    </>
  );
}

function EmptyThread() {
  return (
    <div className="flex flex-col items-center justify-center gap-2 p-10 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
        <MessagesSquare className="h-6 w-6" />
      </div>
      <p className="text-sm font-medium">No conversation selected</p>
      <p className="text-xs text-muted-foreground">Pick a thread on the left to open the conversation.</p>
    </div>
  );
}
